import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { EditingDevice, FourSideStylingEntity, WidgetDeviceView } from 'app/campaign/widgets.model';

@Component({
  selector: 'app-margin-padding',
  templateUrl: './margin-padding.component.html',
  styleUrl: './margin-padding.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MarginPaddingComponent {
  @Input() customClass: string;
  @Input() set setCurrentDeviceView(currentView) {
    if (currentView) {
      this.currentDeviceView = currentView;
      this.currentDevice =
        currentView === WidgetDeviceView.Mobile && (!this.isMobileFirst || !this.mobileEqualsDesktop)
          ? EditingDevice.Mobile
          : EditingDevice.Any;
      this.disableCheckboxAnimation = true;
      setTimeout(() => {
        this.disableCheckboxAnimation = false;
      }, 0);
      this.initializeValues();
    }
  }
  @Input() set setComponent(value) {
    if (!value.component || !value.nameProp) return;
    this.component = value.component;
    this.nameProp = value.nameProp;
    this.namePropMobile = value.namePropMobile;
    if (value.nameMobileSettings) this.nameMobileSettings = value.nameMobileSettings;
    this.initializeValues();
  }
  @Input() title: string;
  @Input() isCheckBox: boolean;
  @Input() isV2 = false;
  @Input() isMobileFirst: boolean;
  @Input() brandSettingValue: FourSideStylingEntity;
  @Output() emitUpdatesOnComponent = new EventEmitter();
  public nameProp: string;
  public namePropMobile: string;
  public nameMobileSettings = 'mobile';
  public currentDeviceView: WidgetDeviceView;
  public component: any;
  public WidgetDeviceViews = WidgetDeviceView;
  public propIsSet: boolean;
  public marginPaddingIsAllRound = true;
  public currentMarginPadding: FourSideStylingEntity;
  public currentDevice: EditingDevice;
  public EditingDevice = EditingDevice;
  public disableCheckboxAnimation = false;
  public isLinked = true;
  public v2Top: number = 0;
  public v2Right: number = 0;
  public v2Bottom: number = 0;
  public v2Left: number = 0;

  public shorthand: Object = {
    top: true,
    right: true,
    bottom: true,
    left: true,
  };

  private initializeValues(): void {
    if (!this.component) {
      return;
    }
    if (!this.currentDevice) {
      this.currentDevice =
        this.currentDeviceView === WidgetDeviceView.Mobile ? EditingDevice.Mobile : EditingDevice.Any;
    }
    if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile) {
      const mobileOverride = this.component[this.nameMobileSettings]?.[this.namePropMobile];
      const settings = mobileOverride || this.component[this.nameProp] || this.brandSettingValue;
      this.propIsSet = (!!settings || this.brandSettingValue !== undefined) && settings?.roundValue !== '0px';
      this.currentMarginPadding = {} as FourSideStylingEntity;
      if (this.propIsSet) {
        this.marginPaddingIsAllRound = this.isMarginPaddingAllRound(settings);
        if (this.marginPaddingIsAllRound) {
          this.currentMarginPadding.roundValue = settings?.roundValue
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.roundValue)
            : '0';
        } else {
          this.currentMarginPadding.top = settings?.top
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.top)
            : '0';
          this.currentMarginPadding.right = settings?.right
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.right)
            : '0';
          this.currentMarginPadding.bottom = settings?.bottom
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.bottom)
            : '0';
          this.currentMarginPadding.left = settings?.left
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.left)
            : '0';
        }
      } else {
        const fallbackSettings =
          this.brandSettingValue ||
          (this.component[this.nameProp]?.roundValue !== '0px' ? this.component[this.nameProp] : null);
        if (fallbackSettings) {
          this.marginPaddingIsAllRound = this.isMarginPaddingAllRound(fallbackSettings);
          if (this.marginPaddingIsAllRound) {
            this.currentMarginPadding.roundValue = fallbackSettings.roundValue
              ? WidgetComponentHelpers.extractNumberFromPixelString(fallbackSettings.roundValue)
              : '4';
          } else {
            this.currentMarginPadding.top = fallbackSettings.top
              ? WidgetComponentHelpers.extractNumberFromPixelString(fallbackSettings.top)
              : '4';
            this.currentMarginPadding.right = fallbackSettings.right
              ? WidgetComponentHelpers.extractNumberFromPixelString(fallbackSettings.right)
              : '4';
            this.currentMarginPadding.bottom = fallbackSettings.bottom
              ? WidgetComponentHelpers.extractNumberFromPixelString(fallbackSettings.bottom)
              : '4';
            this.currentMarginPadding.left = fallbackSettings.left
              ? WidgetComponentHelpers.extractNumberFromPixelString(fallbackSettings.left)
              : '4';
          }
        } else {
          this.currentMarginPadding = { roundValue: '4' } as FourSideStylingEntity;
        }
      }
    } else {
      this.propIsSet =
        (!!this.component[this.nameProp] || this.brandSettingValue !== undefined) &&
        this.component[this.nameProp]?.roundValue !== '0px';
      this.currentMarginPadding = {} as FourSideStylingEntity;
      const settings = this.component[this.nameProp] || this.brandSettingValue;
      if (this.propIsSet) {
        this.marginPaddingIsAllRound = this.isMarginPaddingAllRound(settings);
        if (this.marginPaddingIsAllRound) {
          this.currentMarginPadding.roundValue = settings?.roundValue
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.roundValue)
            : '0';
        } else {
          this.currentMarginPadding.top = settings?.top
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.top)
            : '0';
          this.currentMarginPadding.right = settings?.right
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.right)
            : '0';
          this.currentMarginPadding.bottom = settings?.bottom
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.bottom)
            : '0';
          this.currentMarginPadding.left = settings?.left
            ? WidgetComponentHelpers.extractNumberFromPixelString(settings.left)
            : '0';
        }
      } else {
        this.currentMarginPadding = { roundValue: '4' } as FourSideStylingEntity;
      }
    }
    if (this.isV2) {
      this.initializeV2Values();
    }
  }

  private initializeV2Values(): void {
    const isMobile = this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile;
    const settings: FourSideStylingEntity = isMobile
      ? this.component[this.nameMobileSettings]?.[this.namePropMobile] ||
        this.component[this.nameProp] ||
        this.brandSettingValue
      : this.component[this.nameProp] || this.brandSettingValue;

    if (!settings) {
      this.v2Top = 0;
      this.v2Right = 0;
      this.v2Bottom = 0;
      this.v2Left = 0;
      this.isLinked = true;
      return;
    }

    const hasSides = !!(settings.top || settings.right || settings.bottom || settings.left);
    if (!hasSides) {
      const val = settings.roundValue ? +WidgetComponentHelpers.extractNumberFromPixelString(settings.roundValue) : 0;
      this.v2Top = val;
      this.v2Right = val;
      this.v2Bottom = val;
      this.v2Left = val;
      this.isLinked = true;
    } else {
      this.v2Top = +WidgetComponentHelpers.extractNumberFromPixelString(settings.top || '0');
      this.v2Right = +WidgetComponentHelpers.extractNumberFromPixelString(settings.right || '0');
      this.v2Bottom = +WidgetComponentHelpers.extractNumberFromPixelString(settings.bottom || '0');
      this.v2Left = +WidgetComponentHelpers.extractNumberFromPixelString(settings.left || '0');
      this.isLinked = false;
    }
  }

  public toggleLinked(): void {
    this.isLinked = !this.isLinked;
    if (this.isLinked) {
      this.v2Right = this.v2Top;
      this.v2Bottom = this.v2Top;
      this.v2Left = this.v2Top;
      this.updateV2MarginPadding();
    }
  }

  public onV2InputChange(side: string, event?: Event): void {
    if (event) {
      this['v2' + side.charAt(0).toUpperCase() + side.slice(1)] = +(event.target as HTMLInputElement).value;
    }
    if (this.isLinked) {
      const val = this['v2' + side.charAt(0).toUpperCase() + side.slice(1)];
      this.v2Top = val;
      this.v2Right = val;
      this.v2Bottom = val;
      this.v2Left = val;
    }
    this.updateV2MarginPadding();
  }

  private updateV2MarginPadding(): void {
    const isMobile = this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile;

    if (isMobile) {
      if (!this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings] = {};
      }
      if (!this.component[this.nameMobileSettings][this.namePropMobile]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = {} as FourSideStylingEntity;
      }
      const target = this.component[this.nameMobileSettings][this.namePropMobile];
      if (this.isLinked) {
        target.roundValue = this.v2Top + 'px';
        target.top = undefined;
        target.right = undefined;
        target.bottom = undefined;
        target.left = undefined;
      } else {
        target.roundValue = undefined;
        target.top = this.v2Top + 'px';
        target.right = this.v2Right + 'px';
        target.bottom = this.v2Bottom + 'px';
        target.left = this.v2Left + 'px';
      }
    } else {
      if (!this.component[this.nameProp]) {
        this.component[this.nameProp] = {} as FourSideStylingEntity;
      }
      if (this.isLinked) {
        this.component[this.nameProp].roundValue = this.v2Top + 'px';
        this.component[this.nameProp].top = undefined;
        this.component[this.nameProp].right = undefined;
        this.component[this.nameProp].bottom = undefined;
        this.component[this.nameProp].left = undefined;
      } else {
        this.component[this.nameProp].roundValue = undefined;
        this.component[this.nameProp].top = this.v2Top + 'px';
        this.component[this.nameProp].right = this.v2Right + 'px';
        this.component[this.nameProp].bottom = this.v2Bottom + 'px';
        this.component[this.nameProp].left = this.v2Left + 'px';
      }
    }
    this.emitUpdatesOnComponent.emit();
  }

  public removeBrandSettings(): void {
    this.updateV2MarginPadding();
    this.initializeValues();
  }

  private isMarginPaddingAllRound(margin: FourSideStylingEntity): boolean {
    if (!margin) {
      return true;
    }
    return !margin.top && !margin.right && !margin.bottom && !margin.left;
  }

  public updateEditingDevice(emittedData): void {
    if (emittedData?.currentDevice) this.currentDevice = emittedData.currentDevice;
    if (emittedData?.skipEmitting) return;
    this.initializeValues();
    if (!this.component) return;
    if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Any) {
      if (this.currentDevice === EditingDevice.Any && this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = undefined;
      }
      if (!emittedData?.skipEmitting) {
        this.emitUpdatesOnComponent.emit();
      }
    }
  }

  public selectMargin(side): void {
    Object.keys(this.shorthand).forEach((key) => {
      this.shorthand[key] = key == side;
    });
  }

  public selectAllMargin(selected?: boolean): void {
    Object.keys(this.shorthand).forEach((key) => {
      this.shorthand[key] = selected;
    });
  }

  public toggleSetting(): void {
    if (this.propIsSet) {
      if (this.currentDeviceView === WidgetDeviceView.Mobile) {
        if (!this.component[this.nameMobileSettings]) {
          this.component[this.nameMobileSettings] = {};
        }
        this.component[this.nameMobileSettings][this.namePropMobile] = {} as FourSideStylingEntity;
        if (this.isMarginPaddingAllRound(this.currentMarginPadding)) {
          this.component[this.nameMobileSettings][this.namePropMobile].roundValue =
            (this.currentMarginPadding.roundValue || '4') + 'px';
        } else {
          this.component[this.nameMobileSettings][this.namePropMobile].top =
            (this.currentMarginPadding.top || '4') + 'px';
          this.component[this.nameMobileSettings][this.namePropMobile].right =
            (this.currentMarginPadding.right || '4') + 'px';
          this.component[this.nameMobileSettings][this.namePropMobile].bottom =
            (this.currentMarginPadding.bottom || '4') + 'px';
          this.component[this.nameMobileSettings][this.namePropMobile].left =
            (this.currentMarginPadding.left || '4') + 'px';
        }
      } else {
        const desktopIs0px = this.component[this.nameProp]?.roundValue === '0px';
        const mobileIs0px = this.component[this.nameMobileSettings]?.[this.namePropMobile]?.roundValue === '0px';
        const bothAre0px = desktopIs0px && mobileIs0px;
        this.component[this.nameProp] = {} as FourSideStylingEntity;
        if (this.isMarginPaddingAllRound(this.currentMarginPadding)) {
          this.component[this.nameProp].roundValue = (this.currentMarginPadding.roundValue || 0) + 'px';
        } else {
          this.component[this.nameProp].top = (this.currentMarginPadding.top || 0) + 'px';
          this.component[this.nameProp].right = (this.currentMarginPadding.right || 0) + 'px';
          this.component[this.nameProp].bottom = (this.currentMarginPadding.bottom || 0) + 'px';
          this.component[this.nameProp].left = (this.currentMarginPadding.left || 0) + 'px';
        }
        if (bothAre0px && this.component[this.nameMobileSettings]?.[this.namePropMobile]) {
          this.component[this.nameMobileSettings][this.namePropMobile] = undefined;
        }
        if (this.brandSettingValue) this.applyBrandSettings();
      }
    } else {
      if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile) {
        if (!this.component[this.nameMobileSettings]) {
          this.component[this.nameMobileSettings] = {};
        }
        this.component[this.nameMobileSettings][this.namePropMobile] = { roundValue: '0px' } as FourSideStylingEntity;
      } else {
        if (this.brandSettingValue === undefined) {
          this.component[this.nameProp] = null;
        } else {
          this.component[this.nameProp] = { roundValue: '0px' } as FourSideStylingEntity;
        }
      }
    }
    this.emitUpdatesOnComponent.emit();
  }

  public setMarginPaddingIsAllRound(marginPaddingIsAllRound: boolean): void {
    this.marginPaddingIsAllRound = marginPaddingIsAllRound;
    if (this.marginPaddingIsAllRound) {
      if (!this.currentMarginPadding.roundValue) {
        if (this.currentMarginPadding?.top) {
          this.currentMarginPadding.roundValue = this.currentMarginPadding.top;
        } else {
          this.currentMarginPadding.roundValue = '4';
        }
      }
      this.updateAllRoundMarginSize();
    } else {
      this.selectAllMargin(true);
      if (
        !this.currentMarginPadding.top &&
        !this.currentMarginPadding.right &&
        !this.currentMarginPadding.bottom &&
        !this.currentMarginPadding.left
      ) {
        const newValue = this.currentMarginPadding?.roundValue || '0';
        this.currentMarginPadding = {
          top: newValue,
          right: newValue,
          bottom: newValue,
          left: newValue,
        } as FourSideStylingEntity;
      }
      this.updateSideMarginSizes();
    }
  }

  public updateAllRoundMarginSize(): void {
    if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile) {
      if (!this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings] = {};
      }
      if (!this.component[this.nameMobileSettings][this.namePropMobile]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = {};
      }
      this.component[this.nameMobileSettings][this.namePropMobile].roundValue =
        (this.currentMarginPadding.roundValue || 0) + 'px';
      this.component[this.nameMobileSettings][this.namePropMobile].top = undefined;
      this.component[this.nameMobileSettings][this.namePropMobile].right = undefined;
      this.component[this.nameMobileSettings][this.namePropMobile].bottom = undefined;
      this.component[this.nameMobileSettings][this.namePropMobile].left = undefined;
    } else {
      const desktopIs0px = this.component[this.nameProp]?.roundValue === '0px';
      const mobileIs0px = this.component[this.nameMobileSettings]?.[this.namePropMobile]?.roundValue === '0px';
      const bothAre0px = desktopIs0px && mobileIs0px;
      if (!this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings] = {};
      }
      if (!this.component[this.nameProp]) this.component[this.nameProp] = {};
      this.component[this.nameProp].roundValue = (this.currentMarginPadding.roundValue || 0) + 'px';
      this.component[this.nameProp].top = undefined;
      this.component[this.nameProp].right = undefined;
      this.component[this.nameProp].bottom = undefined;
      this.component[this.nameProp].left = undefined;
      if (bothAre0px && this.component[this.nameMobileSettings][this.namePropMobile]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = undefined;
      }
    }
    this.emitUpdatesOnComponent.emit();
  }

  public updateSideMarginSizes(): void {
    if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile) {
      if (!this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings] = {};
      }
      if (!this.component[this.nameMobileSettings][this.namePropMobile]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = {};
      }
      this.component[this.nameMobileSettings][this.namePropMobile].roundValue = undefined;
      this.component[this.nameMobileSettings][this.namePropMobile].top = (this.currentMarginPadding.top || 0) + 'px';
      this.component[this.nameMobileSettings][this.namePropMobile].right =
        (this.currentMarginPadding.right || 0) + 'px';
      this.component[this.nameMobileSettings][this.namePropMobile].bottom =
        (this.currentMarginPadding.bottom || 0) + 'px';
      this.component[this.nameMobileSettings][this.namePropMobile].left = (this.currentMarginPadding.left || 0) + 'px';
    } else {
      const desktopIs0px = this.component[this.nameProp]?.roundValue === '0px';
      const mobileIs0px = this.component[this.nameMobileSettings]?.[this.namePropMobile]?.roundValue === '0px';
      const bothAre0px = desktopIs0px && mobileIs0px;
      if (!this.component[this.nameMobileSettings]) {
        this.component[this.nameMobileSettings] = {};
      }
      if (!this.component[this.nameProp]) this.component[this.nameProp] = {};
      this.component[this.nameProp].roundValue = undefined;
      this.component[this.nameProp].top = (this.currentMarginPadding.top || 0) + 'px';
      this.component[this.nameProp].right = (this.currentMarginPadding.right || 0) + 'px';
      this.component[this.nameProp].bottom = (this.currentMarginPadding.bottom || 0) + 'px';
      this.component[this.nameProp].left = (this.currentMarginPadding.left || 0) + 'px';
      if (bothAre0px && this.component[this.nameMobileSettings][this.namePropMobile]) {
        this.component[this.nameMobileSettings][this.namePropMobile] = undefined;
      }
    }
    this.emitUpdatesOnComponent.emit();
  }

  public applyBrandSettings(): void {
    if (!this.component.mobile) this.component.mobile = {};
    if (this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile) {
      this.component.mobile[this.nameProp] = this.brandSettingValue;
    } else {
      this.component[this.nameProp] = null;
      this.component.mobile[this.nameProp] = null;
    }
    this.emitUpdatesOnComponent.emit();
  }

  get isBrandSettingsValueApplied() {
    return !!this.brandSettingValue && this.component?.[this.nameProp] === null;
  }

  //START separate logic applying BV to mobile and desktop
  get isBrandSettingsValueAppliedForMobile() {
    return (
      JSON.stringify(this.component?.mobile?.[this.nameProp]) === JSON.stringify(this.brandSettingValue) ||
      !this.component?.mobile?.[this.nameProp]
    );
  }

  get onlyMobileView() {
    return this.currentDeviceView === WidgetDeviceView.Mobile && this.currentDevice === EditingDevice.Mobile;
  }

  get isAppliedToBoth() {
    return this.isBrandSettingsValueApplied && this.isBrandSettingsValueAppliedForMobile;
  }

  get isAppliedToMobileOnly() {
    return (
      this.onlyMobileView &&
      !this.isBrandSettingsValueApplied &&
      JSON.stringify(this.component?.mobile?.[this.nameProp]) === JSON.stringify(this.brandSettingValue) &&
      !!this.component?.mobile?.[this.nameProp]
    );
  }

  get isAppliedToDesktopOnly() {
    return this.isBrandSettingsValueApplied && !this.isBrandSettingsValueAppliedForMobile;
  }

  get canApplyBrandToMobile() {
    return (
      this.onlyMobileView &&
      !!this.component?.mobile?.[this.nameProp] &&
      JSON.stringify(this.component?.mobile?.[this.nameProp]) !== JSON.stringify(this.brandSettingValue)
    );
  }
  //END separate logic applying BV to mobile and desktop

  get mobileEqualsDesktop(): boolean {
    const mobileProp = this.component?.[this.nameMobileSettings]?.[this.namePropMobile];
    if (!mobileProp) return true;
    return JSON.stringify(mobileProp) === JSON.stringify(this.component?.[this.nameProp]);
  }
}
